// components/Reports/BlastingReportContent.js
import React, { useMemo } from "react";
import { useRfi } from "../../../contexts/RfiContext";
import AmbientSurfaceConditionTable from "./AmbientSurfaceConditionTable";

const BlastingReportContent = () => {
  // Get blasting data from Context
  const { stagesData } = useRfi();
  const reduxBlastingData = stagesData.blasting.data;

  // Map Redux data structure to component format
  const blastingData = useMemo(() => {
    if (!reduxBlastingData?.blasting) return null;

    const rfiBlasting = reduxBlastingData.blasting;
    const rfiBlastingImages = reduxBlastingData.blasting_images || [];
    const baseUrl = process.env.REACT_APP_API_BASE_URL;

    // Determine weatherCondition from booleans
    let weatherCondition = "";
    if (rfiBlasting.clear) weatherCondition = "Clear";
    else if (rfiBlasting.rain) weatherCondition = "Rain";
    else if (rfiBlasting.cloudy) weatherCondition = "Cloudy";

    // Determine surfacePreparation from booleans
    let surfacePreparation = "";
    if (rfiBlasting.manual) surfacePreparation = "Manual";
    else if (rfiBlasting.power_tool) surfacePreparation = "Power Tool";
    else if (rfiBlasting.abrasive_blast) surfacePreparation = "Abrasive Blast";

    // Map images to expected format with proper URL handling
    const pictures = rfiBlastingImages.map((img) => {
      let imageUrl = "";

      // Check if image_url exists and prepend base URL if needed
      if (img.image_url) {
        // If image_url already starts with http/https, use it as is
        if (img.image_url.startsWith("http")) {
          imageUrl = img.image_url;
        } else {
          // Otherwise, prepend the base URL
          imageUrl = baseUrl + img.image_url;
        }
      }

      return {
        url: imageUrl,
        id: img.image_id,
      };
    });

    return {
      weatherCondition,
      surfacePreparation,
      typeOfAbrasive: rfiBlasting.type_of_abrasive || "",
      surfaceProfile: rfiBlasting.surface_profile || "",
      remarks: rfiBlasting.remarks || "",
      pictures,
      ambientCondition: {
        time: rfiBlasting.created_at
          ? new Date(rfiBlasting.created_at).toLocaleDateString()
          : "",
        dryBulb: rfiBlasting.dry_bulb ?? "",
        wetBulb: rfiBlasting.wet_bulb ?? "",
        humidity: rfiBlasting.humidity ?? "",
        dewPoint: rfiBlasting.dew_point ?? "",
        substrateTemp: rfiBlasting.substrate_temp ?? "",
        substrateDewPoint: rfiBlasting.substrate_temp_minus_dew_point ?? "",
        status: rfiBlasting.status || "",
        finalInspection: rfiBlasting.final_inspection || "",
      },
    };
  }, [reduxBlastingData]);

  if (!blastingData) {
    return <div>No blasting data available</div>;
  }

  return (
    <div style={{ width: "100%", fontSize: "10px", color: "#222222" }}>
      {/* Weather & Temperature Conditions Section */}
      <div
        style={{
          marginBottom: "16px",
          pageBreakInside: "avoid",
          breakInside: "avoid",
        }}
      >
        <div
          style={{
            fontSize: "12px",
            fontWeight: "bold",
            marginBottom: "10px",
            padding: "6px 10px",
            backgroundColor: "#073642",
            color: "#fff",
            letterSpacing: "0.5px",
          }}
          data-pdf-section="weather-conditions-title"
        >
          WEATHER & TEMPERATURE CONDITIONS
        </div>

        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            border: "1px solid #cbd5e0",
            marginBottom: "12px",
          }}
          data-pdf-section="weather-conditions-table"
        >
          <tbody>
            {/* Row 1: Weather Condition | Surface Preparation */}
            <tr>
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "8px 10px",
                  fontWeight: "bold",
                  width: "25%",
                  backgroundColor: "#f8f9fa",
                }}
              >
                Weather Condition
              </td>
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "8px 10px",
                  width: "25%",
                  backgroundColor: "#fff",
                }}
              >
                {blastingData.weatherCondition || "—"}
              </td>
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "8px 10px",
                  fontWeight: "bold",
                  width: "25%",
                  backgroundColor: "#f8f9fa",
                }}
              >
                Surface Preparation
              </td>
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "8px 10px",
                  width: "25%",
                  backgroundColor: "#fff",
                }}
              >
                {blastingData.surfacePreparation || "—"}
              </td>
            </tr>

            {/* Row 2: Type of Abrasive | Surface Profile */}
            <tr>
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "8px 10px",
                  fontWeight: "bold",
                  backgroundColor: "#f8f9fa",
                }}
              >
                Type of Abrasive
              </td>
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "8px 10px",
                  backgroundColor: "#fff",
                }}
              >
                {blastingData.typeOfAbrasive || "—"}
              </td>
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "8px 10px",
                  fontWeight: "bold",
                  backgroundColor: "#f8f9fa",
                }}
              >
                Surface Profile
              </td>
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "8px 10px",
                  backgroundColor: "#fff",
                }}
              >
                {blastingData.surfaceProfile
                  ? `${blastingData.surfaceProfile} µm`
                  : "—"}
              </td>
            </tr>

            {/* Row 3: Remarks (full width) */}
            <tr>
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "8px 10px",
                  fontWeight: "bold",
                  backgroundColor: "#f8f9fa",
                  verticalAlign: "top",
                }}
              >
                Remarks
              </td>
              <td
                colSpan={3}
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "8px 10px",
                  backgroundColor: "#fff",
                  whiteSpace: "pre-wrap",
                }}
              >
                {blastingData.remarks || "—"}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      {/* End Weather & Temperature Conditions Section */}

      {/* Ambient & Surface Condition Section */}
      <AmbientSurfaceConditionTable data={blastingData.ambientCondition} />

      {/* Pictures Section */}
      {blastingData.pictures && blastingData.pictures.length > 0 && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          {blastingData.pictures.reduce((acc, picture, index) => {
            if (index % 4 === 0) {
              const isFirstRow = index === 0;

              acc.push(
                <div
                  key={`image-row-${index}`}
                  data-pdf-section={`images-section-${index}`}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    width: "100%",
                  }}
                >
                  {/* Add title only for first row */}
                  {isFirstRow && (
                    <div
                      style={{
                        fontWeight: "bold",
                        marginBottom: "10px",
                        fontSize: "11px",
                        color: "#073642",
                      }}
                    >
                      Uploaded Pictures:
                    </div>
                  )}

                  {/* Image row */}
                  <div
                    style={{
                      display: "flex",
                      gap: "12px",
                      width: "100%",
                    }}
                  >
                    {blastingData.pictures
                      .slice(index, index + 4)
                      .map((picture, innerIndex) => (
                        <div
                          key={picture.id || index + innerIndex}
                          style={{
                            flex: "1",
                            minWidth: "0",
                            maxWidth: "calc(25% - 9px)",
                            height: "120px",
                            border: "1px solid #e0e0e0",
                            borderRadius: "4px",
                            overflow: "hidden",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                          }}
                        >
                          <img
                            src={picture.url}
                            alt={`Blasting ${index + innerIndex + 1}`}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                            onError={(e) => {
                              console.error(
                                `Failed to load image: ${picture.url}`
                              );
                              e.target.style.display = "none";
                            }}
                          />
                        </div>
                      ))}
                  </div>
                </div>
              );
            }

            return acc;
          }, [])}
        </div>
      )}
    </div>
  );
};

export default BlastingReportContent;
